import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../../../components/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface RedemptionsChartProps {
  data: { week: string; value: number }[];
  currentRedemptions: number;
  growth: number;
  thisWeek: number;
  lastWeek: number;
  avgWeek: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    [key: string]: unknown;
  }>;
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    const isDarkMode = document.documentElement.classList.contains("dark");
    return (
      <div
        style={{
          backgroundColor: isDarkMode ? "#1f2937" : "#fff",
          border: `1px solid ${isDarkMode ? "#374151" : "#e5e7eb"}`,
          borderRadius: "8px",
          padding: "8px 12px",
        }}
      >
        <p
          style={{
            color: isDarkMode ? "#f3f4f6" : "#111827",
            margin: "0 0 4px 0",
            fontSize: "12px",
          }}
        >
          {label}
        </p>
        <p
          style={{
            color: isDarkMode ? "#10b981" : "#00c49a",
            margin: 0,
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          {payload[0].value.toLocaleString()}
        </p>
      </div>
    );
  }
  return null;
};

export const RedemptionsChart: React.FC<RedemptionsChartProps> = ({
  data,
  currentRedemptions,
  growth,
  thisWeek,
  lastWeek,
  avgWeek,
}) => {
  const [isDarkMode, setIsDarkMode] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const lineColor = isDarkMode ? "#10b981" : "#00c49a";

  return (
    <Card className="bg-white dark:bg-gray-800 outline outline-1 outline-offset-[-1px] outline-gray-200 dark:outline-gray-700 rounded-2xl">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex flex-col gap-1">
            <h3
              className="text-[#004232] dark:text-emerald-400 text-xl font-normal leading-8"
              style={{ fontFamily: "Cal Sans" }}
            >
              Redemptions Trend
            </h3>
            <p
              className="text-gray-500 dark:text-gray-400 text-sm font-normal leading-5"
              style={{ fontFamily: "Segoe UI" }}
            >
              Last 7 weeks
            </p>
          </div>
          <div className="flex flex-col items-end">
            <div
              className="text-gray-900 dark:text-white text-2xl font-normal leading-8 text-right"
              style={{ fontFamily: "Segoe UI" }}
            >
              {currentRedemptions.toLocaleString()}
            </div>
            <div
              className="text-green-600 dark:text-emerald-400 text-sm font-normal leading-5 text-right"
              style={{ fontFamily: "Segoe UI" }}
            >
              ↑ {growth}% growth
            </div>
          </div>
        </div>

        <div className="h-80 pt-2 mb-6">
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <LineChart
              data={data}
              margin={{ top: 8, right: 30, left: 0, bottom: 30 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e5e7eb"
                className="dark:stroke-gray-700"
              />
              <XAxis
                dataKey="week"
                tick={{ fill: "#6b7280", fontSize: 12 }}
                tickLine={{ stroke: "#6b7280" }}
                className="dark:[&_text]:fill-gray-400"
              />
              <YAxis
                tick={{ fill: "#6b7280", fontSize: 12 }}
                tickLine={{ stroke: "#6b7280" }}
                domain={[0, 1600]}
                ticks={[0, 400, 800, 1200, 1600]}
                className="dark:[&_text]:fill-gray-400"
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="value"
                stroke={lineColor}
                strokeWidth={2}
                dot={{ fill: lineColor, r: 4 }}
                activeDot={{ r: 6, fill: lineColor }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="flex justify-center items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px] max-w-96 bg-gray-50 dark:bg-gray-700 rounded-[10px] p-3 flex flex-col gap-1">
            <div
              className="text-center text-gray-500 dark:text-gray-400 text-xs font-normal leading-4"
              style={{ fontFamily: "Segoe UI" }}
            >
              This Week
            </div>
            <div
              className="text-center text-gray-900 dark:text-white text-lg font-normal leading-7"
              style={{ fontFamily: "Segoe UI" }}
            >
              {thisWeek.toLocaleString()}
            </div>
          </div>
          <div className="flex-1 min-w-[200px] max-w-96 bg-gray-50 dark:bg-gray-700 rounded-[10px] p-3 flex flex-col gap-1">
            <div
              className="text-center text-gray-500 dark:text-gray-400 text-xs font-normal leading-4"
              style={{ fontFamily: "Segoe UI" }}
            >
              Last Week
            </div>
            <div
              className="text-center text-gray-900 dark:text-white text-lg font-normal leading-7"
              style={{ fontFamily: "Segoe UI" }}
            >
              {lastWeek.toLocaleString()}
            </div>
          </div>
          <div className="flex-1 min-w-[200px] max-w-96 bg-gray-50 dark:bg-gray-700 rounded-[10px] p-3 flex flex-col gap-1">
            <div
              className="text-center text-gray-500 dark:text-gray-400 text-xs font-normal leading-4"
              style={{ fontFamily: "Segoe UI" }}
            >
              Avg/Week
            </div>
            <div
              className="text-center text-gray-900 dark:text-white text-lg font-normal leading-7"
              style={{ fontFamily: "Segoe UI" }}
            >
              {avgWeek.toLocaleString()}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
